import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Grid, Button, Dropdown, DropdownMenuItem, Chip, Stack, TextField} from 'views/components';
import {Close} from 'views/icons';
import {TimePicker, Translate, translate} from 'views/module';

import moment from 'moment';
import momentTZ from 'moment-timezone';

const Schedule = (props) => {
  const {setIsStepValid, newJob, updateJobSchedule} = props;

  // Get schedule types from translation object
  const scheduleTypesObject = translate('career.create.Schedule.ScheduleTypes');
  const scheduleTypes = Object.keys(scheduleTypesObject);

  // Get days of week from translation object
  const daysOfWeekObject = translate('career.create.Schedule.Day');
  const daysOfWeek = Object.keys(daysOfWeekObject);
  const preferredDayOptions = Object.keys(translate('career.create.Schedule.PreferredDayOptions'));
  const preferredTimeOptions = Object.keys(translate('career.create.Schedule.PreferredTimeOptions'));
  const scheduleType = newJob?.schedule?.type || 'RECURRENCE_EVENT';
  const scheduleData = React.useMemo(() => {
    const sourceSchedule = newJob?.schedule?.schedule || {};

    if (scheduleType === 'LIVEIN_EVENT') {
      return {
        timezone: momentTZ.tz.guess(),
        ...sourceSchedule,
      };
    }

    if (scheduleType === 'FLEXIBLE') {
      return {
        preferredDays: [],
        preferredTimes: [],
        visitDuration: '',
        ...sourceSchedule,
      };
    }

    return {
      weeklySlots: [],
      timezone: momentTZ.tz.guess(),
      ...sourceSchedule,
    };
  }, [scheduleType, newJob?.schedule?.schedule]);

  // Save data to job object whenever state changes
  React.useEffect(() => {
    const dataToSave = {
      type: scheduleType,
      schedule: scheduleData,
    };
    updateJobSchedule(() => dataToSave);
  }, [scheduleData, scheduleType, updateJobSchedule]);

  // Auto-load a default event when opening schedule for the first time
  React.useEffect(() => {
    if (scheduleType === 'RECURRENCE_EVENT' && scheduleData.weeklySlots.length === 0) {
      // Only auto-load if there are no existing slots and we're not loading from saved data
      if (!newJob?.schedule?.schedule?.weeklySlots?.length) {
        const defaultSlot = {
          day: 'MO', // Default to Monday
          startTime: moment().hour(9).minute(0).second(0),
          endTime: moment().hour(17).minute(0).second(0),
        };

        updateJobSchedule((prev) => ({
          ...prev,
          schedule: {
            ...prev.schedule,
            weeklySlots: [defaultSlot],
          },
        }));
      }
    }
  }, [scheduleType, newJob?.schedule?.schedule?.weeklySlots?.length, updateJobSchedule]);

  React.useEffect(() => {
    if (scheduleType === 'RECURRENCE_EVENT') {
      for (const slot of scheduleData.weeklySlots) {
        if (!slot.day || !slot.startTime || !slot.endTime) {
          setIsStepValid(false);
          return;
        }

        if (moment(slot.startTime).isSame(moment(slot.endTime))) {
          setIsStepValid(false);
          return;
        }
      }
    } else if (scheduleType === 'LIVEIN_EVENT') {
      const isGenerallyValid = scheduleData.timezone;
      if (!isGenerallyValid) {
        setIsStepValid(false);
        return;
      }
    } else if (scheduleType === 'FLEXIBLE') {
      if (!scheduleData.visitDuration?.trim()) {
        setIsStepValid(false);
        return;
      }
    }

    setIsStepValid(true);
  }, [setIsStepValid, scheduleType, scheduleData]);

  const updateScheduleData = (func) => {
    updateJobSchedule((prev) => ({
      ...prev,
      schedule: func(prev.schedule || scheduleData),
    }));
  };

  const addWeeklySlot = () => {
    const newSlot = {
      day: 'MO', // Default to Monday
      startTime: moment().hour(9).minute(0).second(0),
      endTime: moment().hour(17).minute(0).second(0),
    };

    updateScheduleData((prevSchedule) => ({
      ...prevSchedule,
      weeklySlots: [...(prevSchedule?.weeklySlots || []), newSlot],
    }));
  };

  const removeWeeklySlot = (slotIndex) => {
    updateScheduleData((prevSchedule) => ({
      ...prevSchedule,
      weeklySlots: (prevSchedule?.weeklySlots || []).filter((_, index) => index !== slotIndex),
    }));
  };

  const updateWeeklySlot = (slotIndex, field, value) => {
    updateScheduleData((prevSchedule) => ({
      ...prevSchedule,
      weeklySlots: (prevSchedule?.weeklySlots || []).map((slot, index) => {
        if (index !== slotIndex) return slot;

        const updatedSlot = {...slot, [field]: value};

        if (field === 'endTime' || field === 'startTime') {
          const startTime = field === 'startTime' ? value : slot.startTime;
          const endTime = field === 'endTime' ? value : slot.endTime;

          if (startTime && endTime) {
            const start = moment(startTime);
            const end = moment(endTime);

            const startTimeOfDay = start.hour() * 60 + start.minute();
            const endTimeOfDay = end.hour() * 60 + end.minute();

            // Set endTime to have the same date as startTime
            updatedSlot.endTime = start.clone().hour(end.hour()).minute(end.minute()).second(0);
            // If it's an overnight shift, add a day
            if (endTimeOfDay <= startTimeOfDay) {
              updatedSlot.endTime.add(1, 'day');
            }
          }
        }

        return updatedSlot;
      }),
    }));
  };

  const toggleFlexibleOption = (field, value, exclusiveValue) => {
    updateScheduleData((prevSchedule) => {
      const currentValues = prevSchedule?.[field] || [];
      const optionOrder = field === 'preferredDays' ? preferredDayOptions : preferredTimeOptions;
      let nextValues;

      if (value === exclusiveValue) {
        nextValues = currentValues.includes(value) ? [] : [value];
      } else {
        const withoutExclusiveValue = currentValues.filter((item) => item !== exclusiveValue);
        nextValues = withoutExclusiveValue.includes(value) ?
          withoutExclusiveValue.filter((item) => item !== value) :
          [...withoutExclusiveValue, value];
        nextValues = optionOrder.filter((item) => nextValues.includes(item));
      }

      return {
        ...prevSchedule,
        [field]: nextValues,
      };
    });
  };

  const updateFlexibleField = (field, value) => {
    updateScheduleData((prevSchedule) => ({
      ...prevSchedule,
      [field]: value,
    }));
  };

  return (
    <Box sx={{p: 2}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 2}}>
        <Translate expression="career.create.Schedule.Title" />
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{mb: 3, textAlign: 'center'}}>
        <Translate expression="career.create.Schedule.Description" />
      </Typography>

      <Box sx={{mb: 3}}>
        <Dropdown
          label={translate('career.create.Schedule.ScheduleTypeLabel')}
          value={scheduleType}
          onChange={(newType) => updateJobSchedule(() => ({
            type: newType,
            schedule: (() => {
              if (newType === 'LIVEIN_EVENT') {
                return {
                  timezone: momentTZ.tz.guess(),
                };
              }

              if (newType === 'FLEXIBLE') {
                return {
                  preferredDays: [],
                  preferredTimes: [],
                  visitDuration: '',
                };
              }

              return {
                weeklySlots: [],
                timezone: momentTZ.tz.guess(),
              };
            })(),
          }))}
        >
          {
            scheduleTypes.map((type) => (
              <DropdownMenuItem key={type} value={type}>
                <Translate expression={`career.create.Schedule.ScheduleType.${type}`} />
              </DropdownMenuItem>
            ))
          }
        </Dropdown>
      </Box>

      {
        scheduleType === 'RECURRENCE_EVENT' &&
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              <Translate expression="career.create.Schedule.WeeklySchedule" />
            </Typography>

            {
              scheduleData.weeklySlots.map((slot, index) => (
                <Box key={index} sx={{mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1}}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Dropdown
                        value={slot.day}
                        onChange={(value) => updateWeeklySlot(index, 'day', value)}
                        label={translate('career.create.Schedule.DayLabel')}
                        renderValue={(selected) => {
                          const isOvernight = moment(slot.endTime).isAfter(moment(slot.startTime), 'day');
                          return (
                            <>
                              <Translate expression={`career.create.Schedule.Day.${selected}`} />
                              {isOvernight && ' (overnight)'}
                            </>
                          );
                        }}
                      >
                        {
                          daysOfWeek.map((day) => (
                            <DropdownMenuItem key={day} value={day}>
                              <Translate expression={`career.create.Schedule.Day.${day}`} />
                            </DropdownMenuItem>
                          ))
                        }
                      </Dropdown>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TimePicker
                        label={translate('career.create.Schedule.StartTimeLabel')}
                        value={slot.startTime}
                        onChange={(time) => updateWeeklySlot(index, 'startTime', time)}
                        textFieldProps={{
                          fullWidth: true,
                          InputLabelProps: {shrink: true},
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TimePicker
                        label={translate('career.create.Schedule.EndTimeLabel')}
                        value={slot.endTime}
                        onChange={(time) => updateWeeklySlot(index, 'endTime', time)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => removeWeeklySlot(index)}
                        sx={{
                          minWidth: '40px',
                          height: '40px',
                          padding: '0',
                        }}
                      >
                        <Close />
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))
            }

            <Button
              variant="outlined"
              onClick={addWeeklySlot}
              sx={{mt: 1}}
            >
              <Translate expression="career.create.Schedule.AddTimeSlot" />
            </Button>
          </Grid>
        </Grid>
      }
      {
        scheduleType === 'LIVEIN_EVENT' &&
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{mt: 2, fontStyle: 'italic'}}>
              <Translate expression="career.create.Schedule.LiveInNote" />
            </Typography>
          </Grid>
        </Grid>
      }
      {
        scheduleType === 'FLEXIBLE' &&
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{maxWidth: '720px', mx: 'auto', width: '100%'}}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Schedule.PreferredDaysTitle" />
                  </Typography>
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25}}>
                    {
                      preferredDayOptions.map((dayOption) => {
                        const isSelected = scheduleData.preferredDays.includes(dayOption);

                        return (
                          <Chip
                            clickable
                            key={dayOption}
                            label={translate(`career.create.Schedule.PreferredDayOptions.${dayOption}`)}
                            onClick={() => toggleFlexibleOption('preferredDays', dayOption, 'ANY_DAY')}
                            variant="outlined"
                            color={isSelected ? 'primary' : 'default'}
                            sx={{
                              'borderRadius': 2.5,
                              'height': 42,
                              'borderStyle': 'dashed',
                              'bgcolor': isSelected ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                              '& .MuiChip-label': {
                                px: 1.75,
                                fontSize: '1rem',
                                fontWeight: isSelected ? 600 : 400,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Schedule.PreferredTimesTitle" />
                  </Typography>
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25}}>
                    {
                      preferredTimeOptions.map((timeOption) => {
                        const isSelected = scheduleData.preferredTimes.includes(timeOption);

                        return (
                          <Chip
                            clickable
                            key={timeOption}
                            label={translate(`career.create.Schedule.PreferredTimeOptions.${timeOption}`)}
                            onClick={() => toggleFlexibleOption('preferredTimes', timeOption, 'ANY_TIME')}
                            variant="outlined"
                            color={isSelected ? 'primary' : 'default'}
                            sx={{
                              'borderRadius': 2.5,
                              'height': 42,
                              'borderStyle': 'dashed',
                              'bgcolor': isSelected ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                              '& .MuiChip-label': {
                                px: 1.75,
                                fontSize: '1rem',
                                fontWeight: isSelected ? 600 : 400,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Schedule.VisitDurationLabel" />
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    placeholder={translate('career.create.Schedule.VisitDurationPlaceholder')}
                    value={scheduleData.visitDuration}
                    onChange={(value) => updateFlexibleField('visitDuration', value)}
                  />
                </Box>

              </Stack>
            </Box>
          </Grid>
        </Grid>
      }
    </Box>
  );
};

Schedule.propTypes = {
  setIsStepValid: PropTypes.func.isRequired,
  newJob: PropTypes.object.isRequired,
  updateJobSchedule: PropTypes.func.isRequired,
};

export default Schedule;
